import type { ChartCard } from '@/features/dashboard/api/schemas'
import { changeFraction } from '@/features/dashboard/format/format'
import { cx } from '@/shared/lib/classNames'
import { formatChartValue } from './lib/tooltipHtml'
import { cellTone, formatCell } from './lib/tableCells'
import styles from './RankBars.module.css'

const TONE_CLASS = {
  positive: styles.good,
  negative: styles.bad,
  flat: '',
  neutral: '',
}

type ChangeProps = {
  value: number
  before: number | null | undefined
  lowerIsBetter: boolean
}

const Change = ({ value, before, lowerIsBetter }: ChangeProps) => {
  const column = {
    id: 'change',
    label: 'Change',
    kind: 'deltaPercent',
    lowerIsBetter,
  } as const
  const ratio = changeFraction(value, before)
  return (
    <td className={cx(styles.change, TONE_CLASS[cellTone(ratio, column)])}>
      {formatCell(ratio, column)}
    </td>
  )
}

type RankBarsProps = {
  card: ChartCard
  /** Largest first. */
  categories: string[]
  values: number[]
  /** The same categories a period earlier; the change column is left out if absent. */
  previous?: (number | null)[] | undefined
  /** A fall is good news (costs): the change's colours swap. */
  lowerIsBetter?: boolean | undefined
}

/**
 * A ranking: one row per category with its bar, value and share of the total
 * (the Figma "Revenue by Segment" and "Costs by Type"). Drawn in the page, not
 * in a chart, so the figures line up as a column.
 */
export const RankBars = ({
  card,
  categories,
  values,
  previous,
  lowerIsBetter = false,
}: RankBarsProps) => {
  const total = values.reduce((sum, v) => sum + v, 0)
  const largest = Math.max(...values.map(Math.abs), 0)
  const hasChange = previous !== undefined
  const previousTotal = hasChange
    ? previous.reduce<number>((sum, v) => sum + (v ?? 0), 0)
    : null
  const money = (v: number) => formatChartValue(v, card.unit, card.format, 1)
  const share = (v: number) =>
    `${Math.round((total === 0 ? 0 : v / total) * 100)}%`

  return (
    <table className={cx(styles.table, hasChange && styles.withChange)}>
      <caption className={styles.hidden}>{card.name} ranked</caption>
      <thead className={styles.hidden}>
        <tr>
          <th scope='col'>Category</th>
          <th scope='col'>Relative size</th>
          <th scope='col'>Value</th>
          <th scope='col'>Share</th>
          {hasChange && <th scope='col'>Change</th>}
        </tr>
      </thead>
      <tbody>
        {categories.map((category, i) => {
          const value = values[i] ?? 0
          return (
            <tr key={`${category}-${i}`} className={styles.row}>
              <th scope='row' className={styles.label}>
                {category}
              </th>
              <td className={styles.track} aria-hidden='true'>
                <span
                  className={styles.bar}
                  style={{
                    width: `${largest === 0 ? 0 : (Math.abs(value) / largest) * 100}%`,
                  }}
                />
              </td>
              <td className={styles.value}>{money(value)}</td>
              <td className={styles.share}>{share(value)}</td>
              {hasChange && (
                <Change
                  value={value}
                  before={previous[i]}
                  lowerIsBetter={lowerIsBetter}
                />
              )}
            </tr>
          )
        })}
      </tbody>
      <tfoot>
        <tr className={styles.row}>
          <th scope='row' className={cx(styles.label, styles.totalLabel)}>
            Total
          </th>
          <td className={styles.value}>{money(total)}</td>
          <td className={styles.share}>{share(total)}</td>
          {hasChange && (
            <Change
              value={total}
              before={previousTotal}
              lowerIsBetter={lowerIsBetter}
            />
          )}
        </tr>
      </tfoot>
    </table>
  )
}
